// features/subscription/SubscriptionPlans.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../context/SubscriptionContext';
import { Dialog, Switch, Transition } from '@headlessui/react';
import { Fragment } from 'react';

const SubscriptionPlans = () => {
  const { t } = useTranslation();
  const {
    subscriptionPlans,
    activeSubscription,
    subscribeToPlan,
    loading,
    error
  } = useSubscription();
  
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [autoRenew, setAutoRenew] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const handleSelectPlan = (plan) => {
    // Don't select the plan if it's already active
    if (activeSubscription && activeSubscription.plan_id === plan.id) {
      return;
    }
    
    setSelectedPlan(plan);
    setPaymentError(null);
    setPaymentSuccess(false);
    
    // Open confirmation modal immediately when a plan is selected
    setIsConfirmModalOpen(true);
  };

  const handleAutoRenewChange = (e) => {
    setAutoRenew(e.target.checked);
  };

  const confirmSubscription = async () => {
    try {
      setProcessingPayment(true);
      setPaymentError(null);
      setIsConfirmModalOpen(false);
      
      // Check if user has active subscriptions that will be cancelled
      const hasActiveSubscriptions = activeSubscription !== null;
      
      // Call the subscribeToPlan function which will redirect to Stripe
      const result = await subscribeToPlan(selectedPlan.id, null, autoRenew);
      
      // If we have a checkoutUrl, redirect the user to Stripe Checkout
      if (result.checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = result.checkoutUrl;
      } else {
        // This will only execute if there's no redirect (e.g., in test mode)
        if (hasActiveSubscriptions) {
          // Show success message that includes information about cancellation
          setPaymentSuccess(t('subscription.subscriptionSuccessWithCancellation'));
        } else {
          setPaymentSuccess(t('subscription.subscriptionSuccess'));
        }
        
        setSelectedPlan(null);
      }
    } catch (err) {
      setPaymentError(err.message || t('subscription.subscriptionFailed'));
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading && subscriptionPlans.length === 0) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" aria-label={t('common.loading')}></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8 text-center">{t('subscription.plans')}</h1>
      
      {error && (
        <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {paymentSuccess && (
        <div className="bg-green-100 dark:bg-green-900 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-200 px-4 py-3 rounded mb-4">
          {paymentSuccess}
        </div>
      )}
      
      {activeSubscription && (
        <div className="bg-blue-100 dark:bg-blue-900 border border-blue-400 dark:border-blue-700 text-blue-700 dark:text-blue-200 px-4 py-3 rounded mb-6">
          <p className="font-semibold">{t('subscription.activeSubscription')}</p>
          <p>{t('subscription.planName')}: {activeSubscription.plan_name}</p>
          <p>{t('subscription.validUntil')}: {new Date(activeSubscription.end_date).toLocaleDateString()}</p>
          <p>{t('subscription.autoRenew')}: {activeSubscription.auto_renew ? t('common.yes') : t('common.no')}</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {subscriptionPlans.map((plan) => {
          const isCurrentPlan = activeSubscription && activeSubscription.plan_id === plan.id;
          
          return (
            <div
              key={plan.id}
              className={`border rounded-lg p-6 shadow-sm transition-all dark:bg-gray-800 dark:border-gray-700 ${
                isCurrentPlan
                  ? 'border-primary-500 ring-2 ring-primary-500 dark:ring-primary bg-primary-50 dark:bg-primary-dark/20'
                  : selectedPlan?.id === plan.id
                    ? 'border-primary-500 ring-2 ring-primary-500 dark:ring-primary'
                    : 'hover:shadow-md dark:hover:shadow-lg dark:hover:shadow-gray-900'
              }`}
              onClick={() => !isCurrentPlan && handleSelectPlan(plan)}
            >
              {isCurrentPlan && (
                <div className="absolute top-4 right-4 bg-primary text-white text-xs font-bold px-2 py-1 rounded-full dark:bg-primary-dark">
                  {t('subscription.currentPlan')}
                </div>
              )}
              <h2 className="text-xl font-bold mb-2 dark:text-white">{plan.name}</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">{plan.description}</p>
              <div className="text-3xl font-bold mb-4 dark:text-white">
                ${parseFloat(plan.price).toFixed(2)} <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/ {t('subscription.month')}</span>
              </div>
              <ul className="mb-6">
                <li className="flex items-center mb-2">
                  <svg className="w-5 h-5 text-green-500 dark:text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="dark:text-gray-200">{plan.credits_per_month} {t('subscription.creditsPerMonth')}</span>
                </li>
                {plan.features && (() => {
                  try {
                    const features = typeof plan.features === 'string'
                      ? JSON.parse(plan.features)
                      : (Array.isArray(plan.features) ? plan.features : []);
                    
                    return features.map((feature, index) => (
                      <li key={index} className="flex items-center mb-2">
                        <svg className="w-5 h-5 text-green-500 dark:text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="dark:text-gray-200">{t(`subscription.features.${feature}`)}</span>
                      </li>
                    ));
                  } catch (error) {
                    console.error('Error parsing features:', error);
                    return null;
                  }
                })()}
              </ul>
              <button
                className={`w-full py-2 px-4 rounded ${
                  isCurrentPlan
                    ? 'bg-gray-300 text-gray-600 dark:bg-gray-600 dark:text-gray-400 cursor-not-allowed'
                    : selectedPlan?.id === plan.id
                      ? 'bg-primary hover:bg-primary-dark text-white dark:bg-primary-dark dark:hover:bg-primary-dark'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isCurrentPlan) {
                    handleSelectPlan(plan);
                  }
                }}
                disabled={isCurrentPlan}
              >
                {isCurrentPlan
                  ? t('subscription.currentPlan')
                  : selectedPlan?.id === plan.id
                    ? t('subscription.selected')
                    : t('subscription.select')}
              </button>
            </div>
          );
        })}
      </div>
      
      {selectedPlan && paymentError && (
        <div className="mt-4 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded">
          {paymentError}
        </div>
      )}
      
      {/* Confirmation Modal */}
      <Transition appear show={isConfirmModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsConfirmModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 dark:bg-black/50" aria-hidden="true" />
          </Transition.Child>
          
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="mx-auto max-w-md rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl transform transition-all">
                <Dialog.Title className="text-xl font-bold mb-4 dark:text-white">
                  {activeSubscription
                    ? t('subscription.switchPlan')
                    : t('subscription.confirmSubscription')}
                </Dialog.Title>
                
                <div className="mb-6">
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    {activeSubscription
                      ? t('subscription.switchPlanMessage', {
                          currentPlan: activeSubscription.plan_name,
                          newPlan: selectedPlan?.name,
                          price: selectedPlan ? `$${parseFloat(selectedPlan.price).toFixed(2)}` : ''
                        })
                      : t('subscription.confirmSubscriptionMessage', {
                          planName: selectedPlan?.name,
                          price: selectedPlan ? `$${parseFloat(selectedPlan.price).toFixed(2)}` : ''
                        })}
                  </p>
                  
                  <div className="p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 mb-4">
                    <h3 className="font-semibold text-lg mb-2 dark:text-white">{t('subscription.planDetails')}</h3>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium dark:text-gray-300">{t('subscription.planName')}</span>
                      <span className="dark:text-gray-300">{selectedPlan?.name}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium dark:text-gray-300">{t('subscription.price')}</span>
                      <span className="dark:text-gray-300">
                        ${selectedPlan ? parseFloat(selectedPlan.price).toFixed(2) : '0.00'} / {t('subscription.month')}
                      </span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium dark:text-gray-300">{t('subscription.credits')}</span>
                      <span className="dark:text-gray-300">{selectedPlan?.credits_per_month} {t('subscription.creditsPerMonth')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium dark:text-gray-300">{t('subscription.autoRenew')}</span>
                      <span className="dark:text-gray-300">{autoRenew ? t('common.yes') : t('common.no')}</span>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <div className="flex items-center mb-2">
                      <Switch
                        checked={autoRenew}
                        onChange={setAutoRenew}
                        className={`${
                          autoRenew ? 'bg-primary dark:bg-primary-dark' : 'bg-gray-300 dark:bg-gray-600'
                        } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                      >
                        <span
                          className={`${
                            autoRenew ? 'translate-x-6' : 'translate-x-1'
                          } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                      </Switch>
                      <span className="ml-3 text-gray-700 dark:text-gray-300">{t('subscription.autoRenew')}</span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {t('subscription.autoRenewDescription')}
                    </p>
                  </div>
                  
                  {activeSubscription && (
                    <div className="p-4 border border-amber-300 rounded bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
                      <h3 className="font-semibold text-amber-800 dark:text-amber-300 mb-2">{t('subscription.importantNote')}</h3>
                      <p className="text-amber-700 dark:text-amber-200 text-sm">
                        {t('subscription.planSwitchWarning')}
                      </p>
                    </div>
                  )}
                  
                  <div className="p-4 border border-blue-300 rounded bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700 mt-4">
                    <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">{t('subscription.paymentInfo')}</h3>
                    <p className="text-blue-700 dark:text-blue-200 text-sm">
                      {t('subscription.stripeCheckoutInfo')}
                    </p>
                  </div>
                </div>
                
                {paymentError && (
                  <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded mb-4">
                    {paymentError}
                  </div>
                )}
                
                <div className="flex space-x-4">
                  <button
                    className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200"
                    onClick={() => setIsConfirmModalOpen(false)}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    className="flex-1 py-2 px-4 bg-primary hover:bg-primary-dark text-white rounded dark:bg-primary-dark dark:hover:bg-primary-dark"
                    onClick={confirmSubscription}
                    disabled={processingPayment}
                  >
                    {processingPayment
                      ? t('common.processing')
                      : activeSubscription
                        ? t('subscription.switchNow')
                        : t('subscription.proceedToCheckout')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default SubscriptionPlans;
